import React, { useState, useEffect, useRef } from 'react';
import {
  FileText,
  Users,
  Beaker,
  MessageSquare,
  GraduationCap,
  Handshake,
  Briefcase,
  BookOpen,
  Zap,
  ArrowUpRight,
  Star,
  Clock,
  Bell,
  Sparkles,
  ChevronRight,
  X,
  CheckCircle2,
  UserPlus,
  Heart,
  AtSign,
  Award,
  Check,
  Trash2,
  BadgeCheck,
} from 'lucide-react';
import './DeviceMockup.css';
import ExploreMockupContent from './ExploreMockupContent';
import FeedMockupContent from './FeedMockupContent';
import LabsMockupContent from './LabsMockupContent';
import CommunitiesMockupContent from './CommunitiesMockupContent';
import WorkshopsMockupContent from './WorkshopsMockupContent';
import CollaboratorsMockupContent from './CollaboratorsMockupContent';
import CoursesMockupContent from './CoursesMockupContent';
import FreelanceMockupContent from './FreelanceMockupContent';
import MessagingMockupContent from './MessagingMockupContent';

const DeviceMockup = () => {
  const [activeTab, setActiveTab] = useState('explore'); // 'explore', 'feed', 'labs', 'communities', 'workshops', 'collaborators', 'freelance', 'courses', 'messaging'
  const [animationPhase, setAnimationPhase] = useState('initial'); // initial, trending, typing, results
  const [searchText, setSearchText] = useState('');
  const [typingComplete, setTypingComplete] = useState(false);
  const [visibleTrendingCards, setVisibleTrendingCards] = useState(0);
  const [visiblePaperCards, setVisiblePaperCards] = useState(0);
  const [visibleFeedPosts, setVisibleFeedPosts] = useState(0);
  const [visibleLabCards, setVisibleLabCards] = useState(0);
  const [showLabInterior, setShowLabInterior] = useState(false);
  const [visibleTasks, setVisibleTasks] = useState(0);
  const [activeInteriorTab, setActiveInteriorTab] = useState('chat'); // 'chat', 'tasks', 'files', 'library', 'calendar'
  // Communities state
  const [visibleCommunityCards, setVisibleCommunityCards] = useState(0);
  const [showCommunityInterior, setShowCommunityInterior] = useState(false);
  const [visibleCommunityItems, setVisibleCommunityItems] = useState(0);
  const [activeCommunityTab, setActiveCommunityTab] = useState('feed'); // 'feed', 'threads', 'members', 'chat'
  // Workshops state
  const [visibleWorkshopItems, setVisibleWorkshopItems] = useState(0);
  const [activeWorkshopTab, setActiveWorkshopTab] = useState('overview'); // 'overview', 'schedule', 'announcements', 'resources', 'discussions', 'participants'
  // Collaborators state
  const [visibleCollaboratorItems, setVisibleCollaboratorItems] = useState(0);
  const [activeCollaboratorTab, setActiveCollaboratorTab] = useState('network'); // 'network', 'discover', 'connections', 'requests'
  // Courses state
  const [visibleCourseItems, setVisibleCourseItems] = useState(0);
  const [activeCourseTab, setActiveCourseTab] = useState('overview'); // 'overview', 'curriculum', 'assignments', 'discussions', 'resources', 'certificates'
  // Freelance state
  const [visibleFreelanceItems, setVisibleFreelanceItems] = useState(0);
  const [activeFreelanceTab, setActiveFreelanceTab] = useState('browse'); // 'browse', 'my-tasks', 'applications', 'experts', 'earnings'
  // Messaging state
  const [visibleMessagingItems, setVisibleMessagingItems] = useState(0);
  const [activeMessagingTab, setActiveMessagingTab] = useState('all'); // 'all', 'online', 'unread', 'groups'
  // Notifications & Messages state
  const [visibleToasts, setVisibleToasts] = useState([]);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [showMessagesDropdown, setShowMessagesDropdown] = useState(false);
  const [notificationCount, setNotificationCount] = useState(5);
  const [messageCount, setMessageCount] = useState(6); // Total unread: Sarah(3) + James(1) + Research Team(2)
  const [activeNotifFilter, setActiveNotifFilter] = useState('all');
  const [animationKey, setAnimationKey] = useState(0);
  const [startFromTab, setStartFromTab] = useState(null); // Track which tab to start from
  const [userClicked, setUserClicked] = useState(false); // Track if user manually clicked
  const typingRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const animationAbortRef = useRef(null); // To abort current animation sequence

  // Notification filter tabs
  const notifFilterTabs = [
    { id: 'all', label: 'All', icon: Bell },
    { id: 'feed', label: 'Feed', icon: Heart },
    { id: 'tasks', label: 'Tasks', icon: CheckCircle2 },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'network', label: 'Network', icon: Users },
  ];

  // Extended Notifications data - matching real app types
  const notifications = [
    // Today
    { id: 1, type: 'mention', icon: AtSign, user: 'Dr. Lena Hoffmann', avatar: 'LH', action: 'mentioned you in', target: 'Neural Networks Discussion', time: '2m ago', color: 'blue', category: 'feed', isNew: true },
    { id: 2, type: 'like', icon: Heart, user: 'Prof. Idris Malik', avatar: 'IM', action: 'liked your paper', target: 'Deep Learning Survey', time: '5m ago', color: 'pink', category: 'feed', isNew: true },
    { id: 3, type: 'comment', icon: MessageSquare, user: 'Dr. Clara Whitfield', avatar: 'CW', action: 'commented on your post', target: 'AI Ethics Research', time: '12m ago', color: 'blue', category: 'feed', isNew: true },
    { id: 4, type: 'follow', icon: UserPlus, user: 'Dr. Tobias Richter', avatar: 'TR', action: 'started following you', target: '', time: '25m ago', color: 'green', category: 'network', isNew: true },
    { id: 5, type: 'collab_request', icon: Users, user: 'Dr. Naima Farooq', avatar: 'NF', action: 'sent you a collaboration request', target: '', time: '45m ago', color: 'indigo', category: 'network', isNew: true, hasActions: true },
    { id: 6, type: 'task_assigned', icon: CheckCircle2, user: 'Climate AI Lab', avatar: 'CL', action: 'assigned you a new task', target: 'Data Analysis Phase 2', time: '1h ago', color: 'indigo', category: 'tasks', isNew: false },
    { id: 7, type: 'room_joined', icon: Users, user: 'Hadia Warraich', avatar: 'H', action: 'joined', target: 'Quantum Computing Research Group', time: '4 days ago', color: 'green', category: 'network', isNew: false },
    { id: 8, type: 'course_enrolled', icon: BookOpen, user: 'Abdul Rehman', avatar: null, avatarImg: true, action: 'You have successfully joined', target: '"Full-Stack Web Development Bootcamp: React, Node &..."', time: '6 days ago', color: 'blue', category: 'feed', isNew: false },
    { id: 9, type: 'message', icon: Bell, user: 'Abdul Rehman', avatar: null, avatarImg: true, action: 'Abdul Rehman: New message', target: '', time: '6 days ago', color: 'slate', category: 'messages', isNew: false },
    { id: 10, type: 'award', icon: Award, user: 'ResearchPro', avatar: 'R+', action: 'Your paper received', target: 'Top Contributor Badge', time: '1 week ago', color: 'amber', category: 'feed', isNew: false },
    // Earlier
    { id: 11, type: 'workshop_reminder', icon: Clock, user: 'ML Workshop', avatar: 'ML', action: 'starts in 1 hour', target: 'Deep Learning Fundamentals', time: '1 week ago', color: 'amber', category: 'tasks', isNew: false },
    { id: 12, type: 'endorsement', icon: Award, user: 'Dr. Hamza Qureshi', avatar: 'HQ', action: 'endorsed your skill', target: 'Machine Learning', time: '2 weeks ago', color: 'indigo', category: 'network', isNew: false },
    { id: 13, type: 'repost', icon: ArrowUpRight, user: 'Prof. Yuki Tanabe', avatar: 'YT', action: 'reposted your paper', target: 'NLP Advances 2024', time: '2 weeks ago', color: 'green', category: 'feed', isNew: false },
    { id: 14, type: 'rating', icon: Star, user: 'Dr. Rupert Ainsley', avatar: 'RA', action: 'gave you a 5-star rating', target: '', time: '3 weeks ago', color: 'amber', category: 'network', isNew: false },
  ];

  // Messages data - SYNCED with MessagingMockupContent conversations
  // These are the conversations that have unread messages
  const messages = [
    { id: 1, user: 'Dr. Lena Hoffmann', initials: 'LH', message: 'Hey! Can we discuss the methodology section of the paper?', time: '2m ago', unread: true, unreadCount: 3, online: true, verified: true, color: 'linear-gradient(135deg, #6366f1 0%, #0284c7 100%)' },
    { id: 2, user: 'Prof. Idris Malik', initials: 'IM', message: 'The results look very promising! 🎉', time: '15m ago', unread: true, unreadCount: 1, online: true, verified: true, typing: true, color: 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)' },
    { id: 5, user: 'Research Team', initials: 'RT', message: 'Meeting scheduled for Friday at 3pm', time: '3h ago', unread: true, unreadCount: 2, online: false, isGroup: true, color: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)' },
  ];

  // Total unread count from all conversations
  const totalUnreadMessages = messages.reduce((sum, m) => sum + (m.unreadCount || 0), 0);

  // Filter notifications based on active tab
  const filteredNotifications = activeNotifFilter === 'all' 
    ? notifications 
    : notifications.filter(n => n.category === activeNotifFilter);

  // Group notifications by time
  const todayNotifications = filteredNotifications.filter(n => n.isNew || n.time.includes('m ago') || n.time.includes('h ago'));
  const earlierNotifications = filteredNotifications.filter(n => !n.isNew && !n.time.includes('m ago') && !n.time.includes('h ago'));

  // Toast notification system
  useEffect(() => {
    const toastNotifications = [
      { id: 't1', type: 'mention', icon: AtSign, text: 'Dr. Lena Hoffmann mentioned you in a discussion', color: 'blue' },
      { id: 't2', type: 'like', icon: Heart, text: 'Your paper received 50+ likes!', color: 'pink' },
      { id: 't3', type: 'follow', icon: UserPlus, text: 'Dr. Fiona Gallagher started following you', color: 'green' },
      { id: 't4', type: 'collab', icon: Users, text: 'New collaboration request from MIT Lab', color: 'indigo' },
      { id: 't5', type: 'award', icon: Award, text: 'Congrats! You earned Top Researcher badge', color: 'amber' },
      { id: 't6', type: 'task', icon: CheckCircle2, text: 'Task "Data Analysis" marked complete', color: 'green' },
      { id: 't7', type: 'workshop', icon: Clock, text: 'Workshop "ML Basics" starts in 30 min', color: 'amber' },
      { id: 't8', type: 'comment', icon: MessageSquare, text: 'New comment on your research post', color: 'blue' },
    ];

    let toastIndex = 0;

    const showNextToast = () => {
      if (toastIndex < toastNotifications.length) {
        const toast = toastNotifications[toastIndex];
        setVisibleToasts(prev => [...prev, { ...toast, visible: true }]);
        
        // Remove toast after 4 seconds
        setTimeout(() => {
          setVisibleToasts(prev => prev.filter(t => t.id !== toast.id));
        }, 4000);
        
        toastIndex++;
      } else {
        toastIndex = 0; // Reset for loop
      }
    };

    // Show first toast after 3 seconds, then every 8 seconds
    const initialTimeout = setTimeout(() => {
      showNextToast();
      toastTimeoutRef.current = setInterval(showNextToast, 8000);
    }, 3000);

    return () => {
      clearTimeout(initialTimeout);
      if (toastTimeoutRef.current) clearInterval(toastTimeoutRef.current);
    };
  }, []);

  // Reset animation state
  const resetAnimation = () => {
    setAnimationPhase('initial');
    setSearchText('');
    setTypingComplete(false);
    setVisibleTrendingCards(0);
    setVisiblePaperCards(0);
    setVisibleFeedPosts(0);
    setVisibleLabCards(0);
    setShowLabInterior(false);
    setVisibleTasks(0);
    setActiveInteriorTab('chat');
    // Communities reset
    setVisibleCommunityCards(0);
    setShowCommunityInterior(false);
    setVisibleCommunityItems(0);
    setActiveCommunityTab('feed');
    // Workshops reset
    setVisibleWorkshopItems(0);
    setActiveWorkshopTab('overview');
    // Collaborators reset
    setVisibleCollaboratorItems(0);
    setActiveCollaboratorTab('network');
    // Courses reset
    setVisibleCourseItems(0);
    setActiveCourseTab('overview');
    // Freelance reset
    setVisibleFreelanceItems(0);
    setActiveFreelanceTab('browse');
    // Messaging reset
    setVisibleMessagingItems(0);
    setActiveMessagingTab('all');
    if (typingRef.current) clearInterval(typingRef.current);
  };

  // Function to trigger messaging animation when navigating from dropdown
  const triggerMessagingAnimation = async () => {
    // Close dropdown and switch tab
    setShowMessagesDropdown(false);
    // Trigger animation from messaging tab
    handleNavClick('messaging');
  };

  // ========================================
  // TAB CLICK HANDLERS - Make interior tabs clickable
  // ========================================
  
  // Labs Interior Tab Click Handler
  const handleLabsTabClick = (tabKey) => {
    // Abort current animation
    if (animationAbortRef.current) {
      animationAbortRef.current.aborted = true;
    }
    // Set the active interior tab immediately
    setActiveInteriorTab(tabKey);
    setVisibleTasks(0);
    // Restart animation from labs module with this interior tab
    setStartFromTab('labs:' + tabKey);
    setAnimationKey(prev => prev + 1);
  };

  // Communities Interior Tab Click Handler
  const handleCommunityTabClick = (tabKey) => {
    if (animationAbortRef.current) {
      animationAbortRef.current.aborted = true;
    }
    setActiveCommunityTab(tabKey);
    setVisibleCommunityItems(0);
    setStartFromTab('communities:' + tabKey);
    setAnimationKey(prev => prev + 1);
  };

  // Workshops Tab Click Handler
  const handleWorkshopTabClick = (tabKey) => {
    if (animationAbortRef.current) {
      animationAbortRef.current.aborted = true;
    }
    setActiveWorkshopTab(tabKey);
    setVisibleWorkshopItems(0);
    setStartFromTab('workshops:' + tabKey);
    setAnimationKey(prev => prev + 1);
  };

  // Collaborators Tab Click Handler
  const handleCollaboratorTabClick = (tabKey) => {
    if (animationAbortRef.current) {
      animationAbortRef.current.aborted = true;
    }
    setActiveCollaboratorTab(tabKey);
    setVisibleCollaboratorItems(0);
    setStartFromTab('collaborators:' + tabKey);
    setAnimationKey(prev => prev + 1);
  };

  // Courses Tab Click Handler
  const handleCourseTabClick = (tabKey) => {
    if (animationAbortRef.current) {
      animationAbortRef.current.aborted = true;
    }
    setActiveCourseTab(tabKey);
    setVisibleCourseItems(0);
    setStartFromTab('courses:' + tabKey);
    setAnimationKey(prev => prev + 1);
  };

  // Freelance Tab Click Handler
  const handleFreelanceTabClick = (tabKey) => {
    if (animationAbortRef.current) {
      animationAbortRef.current.aborted = true;
    }
    setActiveFreelanceTab(tabKey);
    setVisibleFreelanceItems(0);
    setStartFromTab('freelance:' + tabKey);
    setAnimationKey(prev => prev + 1);
  };

  // Messaging Tab Click Handler
  const handleMessagingTabClick = (tabKey) => {
    if (animationAbortRef.current) {
      animationAbortRef.current.aborted = true;
    }
    setActiveMessagingTab(tabKey);
    setVisibleMessagingItems(0);
    setStartFromTab('messaging:' + tabKey);
    setAnimationKey(prev => prev + 1);
  };

  // Define the tab order for cycling
  const tabOrder = ['explore', 'feed', 'labs', 'communities', 'workshops', 'collaborators', 'courses', 'freelance', 'messaging'];

  // Animation sequence - loops continuously with tab switching
  // Now supports starting from any tab based on user interaction
  useEffect(() => {
    let isMounted = true;
    const abortController = { aborted: false };
    animationAbortRef.current = abortController;
    
    // Helper function to run animation for a specific tab
    const animateTab = async (tabKey, interiorTabStart = null) => {
      if (!isMounted || abortController.aborted) return false;
      
      resetAnimation();
      setActiveTab(tabKey);
      
      switch (tabKey) {
        case 'explore':
          // Phase 1: Show trending cards one by one
          await new Promise(r => setTimeout(r, 800));
          if (!isMounted || abortController.aborted) return false;
          setAnimationPhase('trending');
          
          // Animate trending cards appearing (8 cards)
          for (let i = 1; i <= 8; i++) {
            await new Promise(r => setTimeout(r, 90));
            if (!isMounted || abortController.aborted) return false;
            setVisibleTrendingCards(i);
          }

          // Phase 2: Wait then start typing
          await new Promise(r => setTimeout(r, 1200));
          if (!isMounted || abortController.aborted) return false;
          setAnimationPhase('typing');
          
          const searchQuery = "Deep Learning Medical Imaging";
          let index = 0;
          
          await new Promise((resolve) => {
            typingRef.current = setInterval(() => {
              if (!isMounted || abortController.aborted) {
                clearInterval(typingRef.current);
                resolve();
                return;
              }
              if (index <= searchQuery.length) {
                setSearchText(searchQuery.slice(0, index));
                index++;
              } else {
                clearInterval(typingRef.current);
                setTypingComplete(true);
                resolve();
              }
            }, 50);
          });

          // Phase 3: Show results
          await new Promise(r => setTimeout(r, 300));
          if (!isMounted || abortController.aborted) return false;
          setAnimationPhase('results');
          
          // Animate paper cards appearing (4 cards)
          for (let i = 1; i <= 4; i++) {
            await new Promise(r => setTimeout(r, 150));
            if (!isMounted || abortController.aborted) return false;
            setVisiblePaperCards(i);
          }

          // Wait before next tab
          await new Promise(r => setTimeout(r, 2500));
          break;
          
        case 'feed':
          setAnimationPhase('feed');
          await new Promise(r => setTimeout(r, 400));
          if (!isMounted || abortController.aborted) return false;
          
          // Animate feed posts appearing (10 posts)
          for (let i = 1; i <= 10; i++) {
            await new Promise(r => setTimeout(r, 100));
            if (!isMounted || abortController.aborted) return false;
            setVisibleFeedPosts(i);
          }

          await new Promise(r => setTimeout(r, 2500));
          break;
          
        case 'labs':
          setAnimationPhase('labs');
          await new Promise(r => setTimeout(r, 400));
          if (!isMounted || abortController.aborted) return false;
          
          // Animate lab cards appearing (6 cards)
          for (let i = 1; i <= 6; i++) {
            await new Promise(r => setTimeout(r, 100));
            if (!isMounted || abortController.aborted) return false;
            setVisibleLabCards(i);
          }
          
          // Wait then transition to interior view
          await new Promise(r => setTimeout(r, 1500));
          if (!isMounted || abortController.aborted) return false;
          
          // LABS INTERIOR - CYCLE THROUGH TABS
          setShowLabInterior(true);
          setAnimationPhase('labs-interior');
          
          const interiorTabs = ['chat', 'tasks', 'files', 'library', 'calendar'];
          
          // If user clicked a tab, start from that tab, otherwise start from first
          const labsStartIdx = interiorTabStart ? interiorTabs.indexOf(interiorTabStart) : 0;
          const tabsToAnimate = labsStartIdx > 0 
            ? [...interiorTabs.slice(labsStartIdx), ...interiorTabs.slice(0, labsStartIdx)]
            : interiorTabs;
          
          for (const tab of tabsToAnimate) {
            if (!isMounted || abortController.aborted) return false;
            
            setActiveInteriorTab(tab);
            setVisibleTasks(0);
            
            await new Promise(r => setTimeout(r, 200));
            if (!isMounted || abortController.aborted) return false;
            
            const itemCount = tab === 'chat' ? 5 : tab === 'tasks' ? 5 : tab === 'calendar' ? 7 : 5;
            for (let i = 1; i <= itemCount; i++) {
              await new Promise(r => setTimeout(r, 80));
              if (!isMounted || abortController.aborted) return false;
              setVisibleTasks(i);
            }
            
            await new Promise(r => setTimeout(r, 2000));
            if (!isMounted || abortController.aborted) return false;
          }

          await new Promise(r => setTimeout(r, 800));
          break;
          
        case 'communities':
          setShowCommunityInterior(true);
          setAnimationPhase('communities-interior');
          
          await new Promise(r => setTimeout(r, 300));
          if (!isMounted || abortController.aborted) return false;
          
          const communityTabs = ['feed', 'threads', 'members', 'chat'];
          
          // If user clicked a tab, start from that tab
          const commStartIndex = interiorTabStart ? communityTabs.indexOf(interiorTabStart) : 0;
          const commTabsToAnimate = commStartIndex > 0 
            ? [...communityTabs.slice(commStartIndex), ...communityTabs.slice(0, commStartIndex)]
            : communityTabs;
          
          for (const tab of commTabsToAnimate) {
            if (!isMounted || abortController.aborted) return false;
            
            setActiveCommunityTab(tab);
            setVisibleCommunityItems(0);
            
            await new Promise(r => setTimeout(r, 200));
            if (!isMounted || abortController.aborted) return false;
            
            const itemCount = tab === 'feed' ? 5 : tab === 'threads' ? 4 : tab === 'members' ? 7 : 5;
            for (let i = 1; i <= itemCount; i++) {
              await new Promise(r => setTimeout(r, 80));
              if (!isMounted || abortController.aborted) return false;
              setVisibleCommunityItems(i);
            }
            
            await new Promise(r => setTimeout(r, 2500));
            if (!isMounted || abortController.aborted) return false;
          }

          await new Promise(r => setTimeout(r, 800));
          break;
          
        case 'workshops':
          setAnimationPhase('workshops-interior');
          
          await new Promise(r => setTimeout(r, 300));
          if (!isMounted || abortController.aborted) return false;
          
          const workshopTabs = ['overview', 'schedule', 'announcements', 'resources', 'discussions', 'participants'];
          
          // If user clicked a tab, start from that tab
          const wsStartIndex = interiorTabStart ? workshopTabs.indexOf(interiorTabStart) : 0;
          const wsTabsToAnimate = wsStartIndex > 0 
            ? [...workshopTabs.slice(wsStartIndex), ...workshopTabs.slice(0, wsStartIndex)]
            : workshopTabs;
          
          for (const tab of wsTabsToAnimate) {
            if (!isMounted || abortController.aborted) return false;
            
            setActiveWorkshopTab(tab);
            setVisibleWorkshopItems(0);
            
            await new Promise(r => setTimeout(r, 200));
            if (!isMounted || abortController.aborted) return false;
            
            const itemCount = tab === 'overview' ? 3 : tab === 'participants' ? 7 : tab === 'schedule' ? 2 : 5;
            for (let i = 1; i <= itemCount; i++) {
              await new Promise(r => setTimeout(r, 100));
              if (!isMounted || abortController.aborted) return false;
              setVisibleWorkshopItems(i);
            }
            
            await new Promise(r => setTimeout(r, 2000));
            if (!isMounted || abortController.aborted) return false;
          }

          await new Promise(r => setTimeout(r, 800));
          break;
          
        case 'collaborators':
          setAnimationPhase('collaborators-interior');
          
          await new Promise(r => setTimeout(r, 300));
          if (!isMounted || abortController.aborted) return false;
          
          const collaboratorTabs = ['network', 'discover', 'connections', 'requests'];
          
          // If user clicked a tab, start from that tab
          const collabStartIndex = interiorTabStart ? collaboratorTabs.indexOf(interiorTabStart) : 0;
          const collabTabsToAnimate = collabStartIndex > 0 
            ? [...collaboratorTabs.slice(collabStartIndex), ...collaboratorTabs.slice(0, collabStartIndex)]
            : collaboratorTabs;
          
          for (const tab of collabTabsToAnimate) {
            if (!isMounted || abortController.aborted) return false;
            
            setActiveCollaboratorTab(tab);
            setVisibleCollaboratorItems(0);
            
            await new Promise(r => setTimeout(r, 200));
            if (!isMounted || abortController.aborted) return false;
            
            const itemCount = tab === 'network' ? 6 : tab === 'discover' ? 4 : tab === 'connections' ? 5 : 5;
            for (let i = 1; i <= itemCount; i++) {
              await new Promise(r => setTimeout(r, 100));
              if (!isMounted || abortController.aborted) return false;
              setVisibleCollaboratorItems(i);
            }
            
            await new Promise(r => setTimeout(r, 2000));
            if (!isMounted || abortController.aborted) return false;
          }

          await new Promise(r => setTimeout(r, 800));
          break;
          
        case 'courses':
          setAnimationPhase('courses-interior');
          
          await new Promise(r => setTimeout(r, 300));
          if (!isMounted || abortController.aborted) return false;
          
          const courseTabs = ['overview', 'curriculum', 'assignments', 'discussions', 'resources', 'certificates'];
          
          // If user clicked a tab, start from that tab
          const courseStartIndex = interiorTabStart ? courseTabs.indexOf(interiorTabStart) : 0;
          const courseTabsToAnimate = courseStartIndex > 0 
            ? [...courseTabs.slice(courseStartIndex), ...courseTabs.slice(0, courseStartIndex)]
            : courseTabs;
          
          for (const tab of courseTabsToAnimate) {
            if (!isMounted || abortController.aborted) return false;
            
            setActiveCourseTab(tab);
            setVisibleCourseItems(0);
            
            await new Promise(r => setTimeout(r, 200));
            if (!isMounted || abortController.aborted) return false;
            
            const itemCount = tab === 'overview' ? 3 : tab === 'curriculum' ? 5 : tab === 'certificates' ? 5 : 4;
            for (let i = 1; i <= itemCount; i++) {
              await new Promise(r => setTimeout(r, 100));
              if (!isMounted || abortController.aborted) return false;
              setVisibleCourseItems(i);
            }
            
            await new Promise(r => setTimeout(r, 2000));
            if (!isMounted || abortController.aborted) return false;
          }

          await new Promise(r => setTimeout(r, 800));
          break;
          
        case 'freelance':
          setAnimationPhase('freelance-interior');
          
          await new Promise(r => setTimeout(r, 300));
          if (!isMounted || abortController.aborted) return false;
          
          const freelanceTabs = ['browse', 'my-tasks', 'applications', 'experts', 'earnings'];
          
          // If user clicked a tab, start from that tab
          const freelanceStartIndex = interiorTabStart ? freelanceTabs.indexOf(interiorTabStart) : 0;
          const freelanceTabsToAnimate = freelanceStartIndex > 0 
            ? [...freelanceTabs.slice(freelanceStartIndex), ...freelanceTabs.slice(0, freelanceStartIndex)]
            : freelanceTabs;
          
          for (const tab of freelanceTabsToAnimate) {
            if (!isMounted || abortController.aborted) return false;
            
            setActiveFreelanceTab(tab);
            setVisibleFreelanceItems(0);
            
            await new Promise(r => setTimeout(r, 200));
            if (!isMounted || abortController.aborted) return false;
            
            const itemCount = tab === 'browse' ? 7 : tab === 'my-tasks' ? 4 : tab === 'applications' ? 4 : tab === 'experts' ? 5 : 4;
            for (let i = 1; i <= itemCount; i++) {
              await new Promise(r => setTimeout(r, 100));
              if (!isMounted || abortController.aborted) return false;
              setVisibleFreelanceItems(i);
            }
            
            await new Promise(r => setTimeout(r, 2000));
            if (!isMounted || abortController.aborted) return false;
          }

          await new Promise(r => setTimeout(r, 800));
          break;
          
        case 'messaging':
          setAnimationPhase('messaging-interior');
          
          await new Promise(r => setTimeout(r, 300));
          if (!isMounted || abortController.aborted) return false;
          
          const messagingTabs = ['all', 'online', 'unread', 'groups'];
          
          // If user clicked a tab, start from that tab
          const msgStartIndex = interiorTabStart ? messagingTabs.indexOf(interiorTabStart) : 0;
          const msgTabsToAnimate = msgStartIndex > 0 
            ? [...messagingTabs.slice(msgStartIndex), ...messagingTabs.slice(0, msgStartIndex)]
            : messagingTabs;
          
          for (const tab of msgTabsToAnimate) {
            if (!isMounted || abortController.aborted) return false;
            
            setActiveMessagingTab(tab);
            setVisibleMessagingItems(0);
            
            await new Promise(r => setTimeout(r, 200));
            if (!isMounted || abortController.aborted) return false;
            
            const itemCount = 10;
            for (let i = 1; i <= itemCount; i++) {
              await new Promise(r => setTimeout(r, 100));
              if (!isMounted || abortController.aborted) return false;
              setVisibleMessagingItems(i);
            }
            
            await new Promise(r => setTimeout(r, 2500));
            if (!isMounted || abortController.aborted) return false;
          }

          await new Promise(r => setTimeout(r, 800));
          break;
      }
      
      return true; // Animation completed successfully
    };
    
    const sequence = async () => {
      if (!isMounted) return;
      
      // Parse startFromTab - can be "module:interiorTab" format or just module name
      let startModule = null;
      let startInteriorTab = null;
      
      if (startFromTab) {
        if (startFromTab.includes(':')) {
          [startModule, startInteriorTab] = startFromTab.split(':');
        } else {
          startModule = startFromTab;
        }
      }
      
      // Determine starting index based on module
      let startIndex = startModule ? tabOrder.indexOf(startModule) : 0;
      if (startIndex === -1) startIndex = 0;
      
      // Create ordered list starting from the selected module
      const orderedTabs = [
        ...tabOrder.slice(startIndex),
        ...tabOrder.slice(0, startIndex)
      ];
      
      // Run through all tabs in order
      for (let i = 0; i < orderedTabs.length; i++) {
        const tabKey = orderedTabs[i];
        if (!isMounted || abortController.aborted) return;
        
        // Only pass interior tab for the first module (the one user clicked)
        const interiorTab = (i === 0) ? startInteriorTab : null;
        const completed = await animateTab(tabKey, interiorTab);
        if (!completed) return; // Animation was interrupted
      }

      // Wait and restart the loop
      await new Promise(r => setTimeout(r, 1000));
      if (!isMounted || abortController.aborted) return;
      
      // Reset and restart from beginning (or continue from where user started)
      resetAnimation();
      setStartFromTab(null); // Reset to default order for next loop
      setAnimationKey(prev => prev + 1);
    };

    sequence();

    return () => {
      isMounted = false;
      abortController.aborted = true;
      if (typingRef.current) clearInterval(typingRef.current);
    };
  }, [animationKey, startFromTab]);

  const navItems = [
    { icon: FileText, label: 'Explore Papers', key: 'explore' },
    { icon: Zap, label: 'Research Feed', key: 'feed' },
    { icon: Beaker, label: 'Research Labs', key: 'labs' },
    { icon: Users, label: 'Communities', badge: true, key: 'communities' },
    { icon: GraduationCap, label: 'Workshops', badge: true, key: 'workshops' },
    { icon: Handshake, label: 'Collaborators', key: 'collaborators' },
    { icon: Briefcase, label: 'Freelance', key: 'freelance' },
    { icon: BookOpen, label: 'Courses', badge: true, key: 'courses' },
    { icon: MessageSquare, label: 'Messages', badge: true, key: 'messaging' },
  ];

  // Handle nav item click - starts animation from clicked tab
  const handleNavClick = (key) => {
    if (tabOrder.includes(key)) {
      // Abort current animation sequence
      if (animationAbortRef.current) {
        animationAbortRef.current.aborted = true;
      }
      if (typingRef.current) clearInterval(typingRef.current);
      
      // Reset all states
      resetAnimation();
      
      // Set the starting tab and trigger new animation sequence
      setStartFromTab(key);
      setUserClicked(true);
      setAnimationKey(prev => prev + 1);
    }
  };

  return (
    <section className="device-mockup-section">
      <div className="device-mockup-container">
        <div className="window-frame">
          {/* Window Title Bar */}
          <div className="window-titlebar">
            <div className="window-buttons">
              <div className="window-btn window-btn-close"></div>
              <div className="window-btn window-btn-minimize"></div>
              <div className="window-btn window-btn-maximize"></div>
            </div>
            <div className="window-title">
              <span className="window-title-text">
                ResearchPro — {activeTab === 'feed' ? 'Research Feed' : activeTab === 'labs' ? 'Research Labs' : activeTab === 'communities' ? 'Communities' : activeTab === 'workshops' ? 'Workshops' : activeTab === 'collaborators' ? 'Find Collaborators' : activeTab === 'freelance' ? 'Research Marketplace' : activeTab === 'courses' ? 'Courses' : activeTab === 'messaging' ? 'Messages' : 'Explore Papers'}
              </span>
            </div>
            <div className="window-actions">
              <div className="window-action-dot"></div>
              <div className="window-action-dot"></div>
            </div>
          </div>
          
          {/* Window Content */}
          <div className="window-content">
            {/* Sidebar */}
            <aside className="mock-sidebar">
              <div className="mock-sidebar-logo">
                <div className="mock-logo-icon">
                  <img src="/NEW-LOGO.png" alt="ResearchPro" className="mock-logo-img" />
                </div>
                <span className="mock-logo-text">ResearchPro</span>
              </div>
              
              <div className="mock-nav-section">
                <span className="mock-nav-label">DISCOVER</span>
                {navItems.slice(0, 3).map((item, i) => (
                  <div 
                    key={i} 
                    className={`mock-nav-item ${activeTab === item.key ? 'active' : ''} ${item.key === 'explore' || item.key === 'feed' || item.key === 'labs' ? 'clickable' : ''}`}
                    onClick={() => handleNavClick(item.key)}
                  >
                    <item.icon className="mock-nav-icon" size={13} />
                    <span className="mock-nav-text">{item.label}</span>
                    {item.badge && <span className="mock-nav-badge">NEW</span>}
                  </div>
                ))}
              </div>

              <div className="mock-nav-section">
                <span className="mock-nav-label">CONNECT</span>
                {navItems.slice(3, 6).map((item, i) => (
                  <div 
                    key={i} 
                    className={`mock-nav-item ${activeTab === item.key ? 'active' : ''} ${item.key === 'communities' || item.key === 'workshops' || item.key === 'collaborators' ? 'clickable' : ''}`}
                    onClick={() => handleNavClick(item.key)}
                  >
                    <item.icon className="mock-nav-icon" size={13} />
                    <span className="mock-nav-text">{item.label}</span>
                    {item.badge && <span className="mock-nav-badge">NEW</span>}
                  </div>
                ))}
              </div>

              <div className="mock-nav-section">
                <span className="mock-nav-label">LEARN</span>
                {navItems.slice(6, 8).map((item, i) => (
                  <div 
                    key={i} 
                    className={`mock-nav-item ${activeTab === item.key ? 'active' : ''} ${item.key === 'courses' || item.key === 'freelance' ? 'clickable' : ''}`}
                    onClick={() => handleNavClick(item.key)}
                  >
                    <item.icon className="mock-nav-icon" size={13} />
                    <span className="mock-nav-text">{item.label}</span>
                    {item.badge && <span className="mock-nav-badge">NEW</span>}
                  </div>
                ))}
              </div>

              <div className="mock-nav-section">
                <span className="mock-nav-label">UTILITIES</span>
                {navItems.slice(8).map((item, i) => (
                  <div 
                    key={i} 
                    className={`mock-nav-item ${activeTab === item.key ? 'active' : ''} ${item.key === 'messaging' ? 'clickable' : ''}`}
                    onClick={() => handleNavClick(item.key)}
                  >
                    <item.icon className="mock-nav-icon" size={13} />
                    <span className="mock-nav-text">{item.label}</span>
                    {item.badge && <span className="mock-nav-badge">NEW</span>}
                  </div>
                ))}
              </div>

              <div className="mock-user-section">
                <div className="mock-user-avatar">
                  <span>MA</span>
                </div>
                <div className="mock-user-info">
                  <span className="mock-user-name">Mehran Akhtar</span>
                  <span className="mock-user-role">Researcher</span>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="mock-main">
              {/* Top Action Bar with Notifications & Messages */}
              <div className="mock-topbar">
                <div className="mock-topbar-left">
                  <div className="mock-breadcrumb">
                    <span className="mock-breadcrumb-item">Home</span>
                    <ChevronRight size={10} />
                    <span className="mock-breadcrumb-item active">
                      {activeTab === 'feed' ? 'Feed' : activeTab === 'labs' ? 'Labs' : activeTab === 'communities' ? 'Communities' : activeTab === 'workshops' ? 'Workshops' : activeTab === 'collaborators' ? 'Collaborators' : activeTab === 'freelance' ? 'Marketplace' : activeTab === 'courses' ? 'Courses' : activeTab === 'messaging' ? 'Messages' : 'Explore'}
                    </span>
                  </div>
                </div>
                <div className="mock-topbar-right">
                  {/* Messages Icon */}
                  <div className="mock-topbar-icon-wrapper">
                    <button 
                      className={`mock-topbar-icon ${showMessagesDropdown ? 'active' : ''}`}
                      onClick={() => {
                        setShowMessagesDropdown(!showMessagesDropdown);
                        setShowNotificationDropdown(false);
                      }}
                    >
                      <MessageSquare size={14} />
                      {messageCount > 0 && (
                        <span className="mock-icon-badge mock-icon-badge--messages">{messageCount}</span>
                      )}
                    </button>
                    {/* Messages Dropdown */}
                    {showMessagesDropdown && (
                      <div className="mock-dropdown mock-messages-dropdown">
                        <div className="mock-dropdown-header">
                          <span className="mock-dropdown-title">Messages</span>
                          <span className="mock-dropdown-count">{totalUnreadMessages} unread</span>
                        </div>
                        <div className="mock-dropdown-content">
                          {messages.map((msg) => (
                            <div 
                              key={msg.id} 
                              className={`mock-message-item ${msg.unread ? 'unread' : ''}`}
                              onClick={() => triggerMessagingAnimation()}
                            >
                              <div className="mock-message-avatar-wrapper">
                                <div className="mock-message-avatar" style={{ background: msg.color }}>
                                  {msg.isGroup ? <Users size={10} /> : msg.initials}
                                </div>
                                {msg.online && <span className="mock-online-indicator"></span>}
                              </div>
                              <div className="mock-message-content">
                                <div className="mock-message-header">
                                  <span className="mock-message-user">
                                    {msg.user}
                                    {msg.verified && <BadgeCheck size={8} className="mock-verified-icon" />}
                                  </span>
                                  <span className="mock-message-time">{msg.time}</span>
                                </div>
                                <div className="mock-message-preview">
                                  {msg.typing ? (
                                    <span className="mock-typing-indicator">typing<span className="mock-typing-dots">...</span></span>
                                  ) : (
                                    <p className="mock-message-text">{msg.message}</p>
                                  )}
                                </div>
                              </div>
                              {msg.unread && msg.unreadCount > 0 && (
                                <div className="mock-unread-badge">{msg.unreadCount}</div>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="mock-dropdown-footer">
                          <button 
                            className="mock-dropdown-viewall"
                            onClick={() => triggerMessagingAnimation()}
                          >
                            View All Messages
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notification Icon */}
                  <div className="mock-topbar-icon-wrapper">
                    <button 
                      className={`mock-topbar-icon ${showNotificationDropdown ? 'active' : ''}`}
                      onClick={() => {
                        setShowNotificationDropdown(!showNotificationDropdown);
                        setShowMessagesDropdown(false);
                      }}
                    >
                      <Bell size={14} />
                      {notificationCount > 0 && (
                        <span className="mock-icon-badge">{notificationCount}</span>
                      )}
                    </button>
                    {/* Notifications Dropdown */}
                    {showNotificationDropdown && (
                      <div className="mock-dropdown mock-notifications-dropdown">
                        <div className="mock-dropdown-header">
                          <div className="mock-dropdown-header-top">
                            <span className="mock-dropdown-title">Notifications</span>
                            <div className="mock-dropdown-actions">
                              <button className="mock-action-btn mock-action-btn--read" title="Mark all as read">
                                <Check size={10} />
                                <span>Read All</span>
                              </button>
                              <button className="mock-action-btn mock-action-btn--clear" title="Clear all">
                                <Trash2 size={10} />
                                <span>Clear</span>
                              </button>
                            </div>
                          </div>
                          {/* Filter Tabs */}
                          <div className="mock-notif-tabs">
                            {notifFilterTabs.map((tab) => (
                              <button
                                key={tab.id}
                                className={`mock-notif-tab ${activeNotifFilter === tab.id ? 'mock-notif-tab--active' : ''}`}
                                onClick={() => setActiveNotifFilter(tab.id)}
                              >
                                <tab.icon size={10} />
                                <span>{tab.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="mock-dropdown-content">
                          {/* Today section */}
                          {todayNotifications.length > 0 && (
                            <>
                              <div className="mock-notif-section-header">TODAY</div>
                              {todayNotifications.map((notif) => (
                                <div key={notif.id} className={`mock-notification-item ${notif.isNew ? 'mock-notification-item--new' : ''}`}>
                                  {notif.avatarImg ? (
                                    <div className="mock-notif-avatar mock-notif-avatar--img">
                                      <div className="mock-notif-avatar-placeholder" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #14b8a6 100%)' }}>
                                        {notif.avatar || notif.user.charAt(0)}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={`mock-notif-avatar mock-notif-avatar--${notif.color}`}>
                                      {notif.avatar}
                                    </div>
                                  )}
                                  <div className="mock-notification-content">
                                    <p className="mock-notification-text">
                                      <strong>{notif.user}</strong> {notif.action} {notif.target && <span className="mock-notification-target">{notif.target}</span>}
                                    </p>
                                    <span className="mock-notification-time">{notif.time}</span>
                                  </div>
                                  <div className={`mock-notification-icon mock-notification-icon--${notif.color}`}>
                                    <notif.icon size={10} />
                                  </div>
                                  {notif.hasActions && (
                                    <div className="mock-notif-actions">
                                      <button className="mock-notif-action-btn mock-notif-action-btn--accept">Accept</button>
                                      <button className="mock-notif-action-btn mock-notif-action-btn--decline">Decline</button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          )}
                          {/* Earlier section */}
                          {earlierNotifications.length > 0 && (
                            <>
                              <div className="mock-notif-section-header">EARLIER</div>
                              {earlierNotifications.map((notif) => (
                                <div key={notif.id} className="mock-notification-item">
                                  {notif.avatarImg ? (
                                    <div className="mock-notif-avatar mock-notif-avatar--img">
                                      <div className="mock-notif-avatar-placeholder" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #14b8a6 100%)' }}>
                                        {notif.avatar || notif.user.charAt(0)}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={`mock-notif-avatar mock-notif-avatar--${notif.color}`}>
                                      {notif.avatar}
                                    </div>
                                  )}
                                  <div className="mock-notification-content">
                                    <p className="mock-notification-text">
                                      <strong>{notif.user}</strong> {notif.action} {notif.target && <span className="mock-notification-target">{notif.target}</span>}
                                    </p>
                                    <span className="mock-notification-time">{notif.time}</span>
                                  </div>
                                  <div className={`mock-notification-icon mock-notification-icon--${notif.color}`}>
                                    <notif.icon size={10} />
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                          {filteredNotifications.length === 0 && (
                            <div className="mock-notif-empty">
                              <Bell size={20} />
                              <span>No notifications</span>
                            </div>
                          )}
                        </div>
                        <div className="mock-dropdown-footer">
                          <button className="mock-dropdown-viewall">View All Notifications</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Toast Notifications */}
              <div className="mock-toast-container">
                {visibleToasts.map((toast, index) => (
                  <div 
                    key={toast.id} 
                    className={`mock-toast mock-toast--${toast.color}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className={`mock-toast-icon mock-toast-icon--${toast.color}`}>
                      <toast.icon size={12} />
                    </div>
                    <span className="mock-toast-text">{toast.text}</span>
                    <button 
                      className="mock-toast-close"
                      onClick={() => setVisibleToasts(prev => prev.filter(t => t.id !== toast.id))}
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>

              {activeTab === 'feed' ? (
                /* ========== FEED TAB CONTENT ========== */
                <FeedMockupContent 
                  visiblePosts={visibleFeedPosts}
                  animationPhase={animationPhase}
                />
              ) : activeTab === 'labs' ? (
                /* ========== LABS TAB CONTENT ========== */
                <LabsMockupContent 
                  visibleCards={visibleLabCards}
                  animationPhase={animationPhase}
                  showInterior={showLabInterior}
                  visibleTasks={visibleTasks}
                  activeInteriorTab={activeInteriorTab}
                  onTabClick={handleLabsTabClick}
                />
              ) : activeTab === 'communities' ? (
                /* ========== COMMUNITIES TAB CONTENT ========== */
                <CommunitiesMockupContent 
                  visibleCards={visibleCommunityCards}
                  animationPhase={animationPhase}
                  showInterior={showCommunityInterior}
                  visibleItems={visibleCommunityItems}
                  activeInteriorTab={activeCommunityTab}
                  onTabClick={handleCommunityTabClick}
                />
              ) : activeTab === 'workshops' ? (
                /* ========== WORKSHOPS TAB CONTENT ========== */
                <WorkshopsMockupContent 
                  visibleItems={visibleWorkshopItems}
                  animationPhase={animationPhase}
                  activeInteriorTab={activeWorkshopTab}
                  onTabClick={handleWorkshopTabClick}
                />
              ) : activeTab === 'collaborators' ? (
                /* ========== COLLABORATORS TAB CONTENT ========== */
                <CollaboratorsMockupContent 
                  visibleItems={visibleCollaboratorItems}
                  animationPhase={animationPhase}
                  activeInteriorTab={activeCollaboratorTab}
                  onTabClick={handleCollaboratorTabClick}
                />
              ) : activeTab === 'courses' ? (
                /* ========== COURSES TAB CONTENT ========== */
                <CoursesMockupContent 
                  visibleItems={visibleCourseItems}
                  animationPhase={animationPhase}
                  activeInteriorTab={activeCourseTab}
                  onTabClick={handleCourseTabClick}
                />
              ) : activeTab === 'freelance' ? (
                /* ========== FREELANCE TAB CONTENT ========== */
                <FreelanceMockupContent 
                  visibleItems={visibleFreelanceItems}
                  animationPhase={animationPhase}
                  activeInteriorTab={activeFreelanceTab}
                  onTabClick={handleFreelanceTabClick}
                />
              ) : activeTab === 'messaging' ? (
                /* ========== MESSAGING TAB CONTENT ========== */
                <MessagingMockupContent 
                  visibleItems={visibleMessagingItems}
                  animationPhase={animationPhase}
                  activeInteriorTab={activeMessagingTab}
                  onTabClick={handleMessagingTabClick}
                />
              ) : (
                /* ========== EXPLORE TAB CONTENT ========== */
                <ExploreMockupContent
                  animationPhase={animationPhase}
                  searchText={searchText}
                  typingComplete={typingComplete}
                  visibleTrendingCards={visibleTrendingCards}
                  visiblePaperCards={visiblePaperCards}
                />
              )}
            </main>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeviceMockup;
